import React, { useState, useEffect } from 'react';
import {
  Typography,
  Container,
  Box,
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  Divider,
  Breadcrumbs,
  Link,
  CircularProgress,
  Alert,
  Button,
} from '@mui/material';
import {
  History as HistoryIcon,
  Home as HomeIcon,
  NavigateNext as NavigateNextIcon,
} from '@mui/icons-material';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../contexts/AuthContext';
import { BottomNavigation } from '../layout/BottomNavigation';
import { TopBar } from '../layout/TopBar';
import { useNavigate, useParams } from 'react-router-dom';
import { useInventoryItem } from '../hooks/useInventoryItem';
import { getItemLoanHistory, LoanHistoryRecord } from '../services/inventoryLoanHistoryService';
import { EmptyInventoryState } from '../components/inventory/EmptyInventoryState';

export const BrowserHistory: React.FC = () => {
  const { theme } = useTheme();
  const { user } = useAuth();
  const navigate = useNavigate();
  const { itemId } = useParams<{ itemId: string }>();
  const { item, usageHistory, loading, error, itemExists } = useInventoryItem(itemId);
  const [loanHistory, setLoanHistory] = useState<LoanHistoryRecord[]>([]);
  const [loanHistoryLoading, setLoanHistoryLoading] = useState(true);
  const [loanHistoryError, setLoanHistoryError] = useState(false);
  const [loanReloadKey, setLoanReloadKey] = useState(0);

  useEffect(() => {
    let active = true;
    const load = async () => {
      if (!itemId || !user?.uid) {
        setLoanHistoryLoading(false);
        return;
      }
      setLoanHistoryLoading(true);
      setLoanHistoryError(false);
      try {
        const records = await getItemLoanHistory(itemId, user.uid);
        if (active) setLoanHistory(records);
      } catch {
        if (active) {
          setLoanHistory([]);
          setLoanHistoryError(true);
        }
      } finally {
        if (active) setLoanHistoryLoading(false);
      }
    };
    load();
    return () => {
      active = false;
    };
  }, [itemId, user?.uid, loanReloadKey]);

  const displayLoans: LoanHistoryRecord[] = (() => {
    const list = [...loanHistory];
    const active = item?.loan;
    if (active?.borrowerName) {
      const already = list.some((r) => r.status === 'active' && (active.loanId ? r.loanId === active.loanId : false));
      if (!already) {
        list.unshift({
          id: active.loanId || 'active-legacy',
          loanId: active.loanId || 'active-legacy',
          itemId: itemId || '',
          ownerId: user?.uid || '',
          borrowerName: active.borrowerName,
          borrowerGuestId: active.borrowerGuestId,
          borrowerGroup: active.borrowerGroup,
          quantity: active.quantity,
          loanedAt: active.loanedAt,
          dueDate: active.dueDate,
          status: 'active',
        });
      }
    }
    return list;
  })();

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return {
      day: date.getDate().toString().padStart(2, '0'),
      month: new Intl.DateTimeFormat('pt-BR', { month: 'short' }).format(date).toUpperCase(),
    };
  };

  const formatMoment = (value?: string) => {
    if (!value) return '-';
    const dateOnly = /^\d{4}-\d{2}-\d{2}$/.test(value);
    const d = dateOnly ? new Date(`${value}T00:00:00`) : new Date(value);
    if (Number.isNaN(d.getTime())) return '-';
    const datePart = d.toLocaleDateString('pt-BR', { day: '2-digit', month: 'long', year: 'numeric' });
    if (dateOnly) return datePart;
    const timePart = d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
    return `${datePart} - ${timePart}`;
  };

  if (loading) {
    return (
      <>
        <TopBar title="Histórico de uso" icon={<HistoryIcon sx={{ fontSize: '0.875rem' }} />} showBackButton onBackClick={() => navigate(-1)} />
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh', paddingTop: '70px' }}>
          <CircularProgress sx={{ color: theme.colors.primary }} />
        </Box>
      </>
    );
  }

  if (!itemExists) {
    return (
      <>
        <TopBar
          title="Histórico de uso"
          icon={<HistoryIcon sx={{ fontSize: '0.875rem' }} />}
          showBackButton={true}
          onBackClick={() => navigate('/inventory')}
        />
        <Box sx={{ paddingTop: '70px', paddingBottom: '120px' }}>
          <EmptyInventoryState
            title="Item não encontrado"
            description="Este item não existe. Adicione um novo item para começar."
            showDialog={true}
          />
        </Box>
        <BottomNavigation />
      </>
    );
  }

  return (
    <>
      <TopBar
        title="Histórico de uso"
        icon={<HistoryIcon sx={{ fontSize: '0.875rem' }} />}
        showBackButton={true}
        showProfileButton={true}
        onBackClick={() => navigate(-1)}
      />

      <Box sx={{
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: theme.colors.background,
        minHeight: '100vh',
        paddingBottom: '120px',
        paddingTop: '70px',
      }}>
        <Container sx={{ mt: 2 }}>
          {/* Breadcrumbs */}
          <Breadcrumbs
            separator={<NavigateNextIcon fontSize="small" />}
            sx={{
              mb: 3,
              '& .MuiBreadcrumbs-separator': {
                color: theme.colors.text.secondary,
              }
            }}
          >
            <Link
              underline="hover"
              color={theme.colors.text.secondary}
              sx={{
                display: 'flex',
                alignItems: 'center',
                cursor: 'pointer',
                '&:hover': {
                  color: theme.colors.primary,
                }
              }}
              onClick={() => navigate('/')}
            >
              <HomeIcon sx={{ mr: 0.5, fontSize: '1rem' }} />
              Dashboard
            </Link>
            <Link
              underline="hover"
              color={theme.colors.text.secondary}
              sx={{
                cursor: 'pointer',
                '&:hover': {
                  color: theme.colors.primary,
                }
              }}
              onClick={() => navigate('/inventory')}
            >
              Acervo
            </Link>
            <Link
              underline="hover"
              color={theme.colors.text.secondary}
              sx={{
                cursor: 'pointer',
                '&:hover': {
                  color: theme.colors.primary,
                }
              }}
              onClick={() => navigate(`/inventory/${itemId}`)}
            >
              {item?.itemName || 'Item'}
            </Link>
            <Typography
              color={theme.colors.text.primary}
              sx={{ fontWeight: 600 }}
            >
              Histórico de uso
            </Typography>
          </Breadcrumbs>

          {/* Item Summary */}
          {item && (
            <Box sx={{
              display: 'flex',
              alignItems: 'center',
              mb: 3,
              padding: 2,
              backgroundColor: theme.colors.card,
              borderRadius: theme.radii.medium,
              border: `1px solid ${theme.colors.border}`,
            }}>
              <Avatar
                variant="rounded"
                src={item.photoUrl}
                sx={{
                  width: 60,
                  height: 60,
                  borderRadius: theme.radii.medium,
                  backgroundColor: theme.colors.secondary,
                }}
              >
                📦
              </Avatar>
              <Paper
                elevation={0}
                sx={{
                  ml: 2,
                  py: 1,
                  px: 2,
                  borderRadius: theme.radii.medium,
                  backgroundColor: theme.colors.secondary,
                  flexGrow: 1
                }}
              >
                <Typography
                  variant="body1"
                  sx={{
                    color: theme.colors.primary,
                    fontWeight: 'bold',
                  }}
                >
                  Usado em {usageHistory.length} {usageHistory.length === 1 ? 'evento' : 'eventos'}
                </Typography>
              </Paper>
            </Box>
          )}

          {/* History List */}
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
          )}

          {usageHistory.length === 0 ? (
            <Paper
              elevation={0}
              sx={{
                borderRadius: theme.radii.medium,
                backgroundColor: theme.colors.card,
                border: `1px solid ${theme.colors.border}`,
                p: 4,
                textAlign: 'center',
              }}
            >
              <Typography sx={{ color: theme.colors.text.secondary }}>
                Este item ainda não foi usado em nenhum evento.
              </Typography>
            </Paper>
          ) : (
            <Paper
              elevation={0}
              sx={{
                borderRadius: theme.radii.medium,
                backgroundColor: theme.colors.card,
                border: `1px solid ${theme.colors.border}`,
              }}
            >
              <List sx={{ padding: 0 }}>
                {usageHistory.map((historyItem, index) => {
                  const dateInfo = formatDate(historyItem.eventDate);
                  return (
                    <React.Fragment key={historyItem.id}>
                      <ListItem
                        sx={{
                          py: 1.5,
                          px: 2,
                          cursor: 'pointer',
                          '&:hover': {
                            backgroundColor: `${theme.colors.primary}10`,
                          }
                        }}
                        onClick={() => navigate(`/events/${historyItem.eventId}`)}
                      >
                        <ListItemAvatar>
                          <Box sx={{
                            backgroundColor: theme.colors.secondary,
                            borderRadius: theme.radii.small,
                            p: 1,
                            width: 50,
                            height: 50,
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}>
                            <Typography
                              variant="caption"
                              sx={{
                                color: theme.colors.primary,
                                fontWeight: 'bold',
                                lineHeight: 1.1,
                                fontSize: '0.6rem',
                              }}
                            >
                              {dateInfo.day}
                            </Typography>
                            <Typography
                              variant="caption"
                              sx={{
                                color: theme.colors.primary,
                                fontWeight: 'bold',
                                lineHeight: 1.1,
                                fontSize: '0.6rem',
                              }}
                            >
                              {dateInfo.month}
                            </Typography>
                          </Box>
                        </ListItemAvatar>
                        <ListItemText
                          primary={
                            <Typography
                              variant="body1"
                              sx={{ color: theme.colors.text.primary }}
                            >
                              Você usou {historyItem.quantityUsed} {historyItem.quantityUsed === 1 ? 'item' : 'itens'}
                            </Typography>
                          }
                          secondary={
                            <Typography
                              variant="body2"
                              sx={{ color: theme.colors.text.secondary }}
                            >
                              {historyItem.eventName} - Ver evento
                            </Typography>
                          }
                        />
                      </ListItem>
                      {index < usageHistory.length - 1 && (
                        <Divider
                          variant="inset"
                          component="li"
                          sx={{ borderColor: theme.colors.border }}
                        />
                      )}
                    </React.Fragment>
                  );
                })}
              </List>
            </Paper>
          )}

          <Box sx={{ mt: 4 }}>
            <Typography variant="h6" sx={{ color: theme.colors.text.primary, fontWeight: 700, mb: 1.5 }}>
              Histórico de empréstimos
            </Typography>
            {loanHistoryLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                <CircularProgress size={24} sx={{ color: theme.colors.primary }} />
              </Box>
            ) : loanHistoryError ? (
              <Paper
                elevation={0}
                sx={{
                  borderRadius: theme.radii.medium,
                  backgroundColor: theme.colors.card,
                  border: `1px solid ${theme.colors.border}`,
                  p: 4,
                  textAlign: 'center',
                }}
              >
                <Typography sx={{ color: theme.colors.text.secondary, mb: 1 }}>
                  Não foi possível carregar o histórico de empréstimos.
                </Typography>
                <Button onClick={() => setLoanReloadKey((k) => k + 1)} sx={{ color: theme.colors.primary, textTransform: 'none' }}>
                  Tentar novamente
                </Button>
              </Paper>
            ) : displayLoans.length === 0 ? (
              <Paper
                elevation={0}
                sx={{
                  borderRadius: theme.radii.medium,
                  backgroundColor: theme.colors.card,
                  border: `1px solid ${theme.colors.border}`,
                  p: 4,
                  textAlign: 'center',
                }}
              >
                <Typography sx={{ color: theme.colors.text.secondary }}>
                  Nenhum empréstimo registrado.
                </Typography>
              </Paper>
            ) : (
              <Paper
                elevation={0}
                sx={{
                  borderRadius: theme.radii.medium,
                  backgroundColor: theme.colors.card,
                  border: `1px solid ${theme.colors.border}`,
                }}
              >
                <List sx={{ padding: 0 }}>
                  {displayLoans.map((rec, index) => (
                    <React.Fragment key={rec.id}>
                      <ListItem sx={{ py: 1.5, px: 2, alignItems: 'flex-start' }}>
                        <ListItemAvatar>
                          <Avatar sx={{ width: 40, height: 40, bgcolor: theme.colors.secondary, color: theme.colors.primary, fontSize: '0.9rem', fontWeight: 700 }}>
                            {(rec.borrowerName || '?').charAt(0).toUpperCase()}
                          </Avatar>
                        </ListItemAvatar>
                        <ListItemText
                          primary={
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                              <Typography component="span" variant="body1" sx={{ color: theme.colors.text.primary, fontWeight: 600 }}>
                                {rec.borrowerName}
                              </Typography>
                              <Box
                                component="span"
                                sx={{
                                  fontSize: '0.7rem',
                                  fontWeight: 700,
                                  px: 1,
                                  py: 0.25,
                                  borderRadius: theme.radii.small,
                                  backgroundColor: rec.status === 'active' ? `${theme.colors.primary}18` : `${theme.colors.text.secondary}18`,
                                  color: rec.status === 'active' ? theme.colors.primary : theme.colors.text.secondary,
                                }}
                              >
                                {rec.status === 'active' ? 'Emprestado' : 'Devolvido'}
                              </Box>
                            </Box>
                          }
                          secondary={
                            <Typography component="span" variant="body2" sx={{ color: theme.colors.text.secondary, display: 'block' }}>
                              {rec.quantity} unidade(s) · Emprestado em {formatMoment(rec.loanedAt)}
                              {rec.dueDate ? ` · Devolver até ${formatMoment(rec.dueDate)}` : ''}
                              {rec.returnedAt ? ` · Devolvido em ${formatMoment(rec.returnedAt)}` : ''}
                              {rec.borrowerGroup ? ` · Grupo: ${rec.borrowerGroup}` : ''}
                            </Typography>
                          }
                        />
                      </ListItem>
                      {index < displayLoans.length - 1 && (
                        <Divider component="li" sx={{ borderColor: theme.colors.border }} />
                      )}
                    </React.Fragment>
                  ))}
                </List>
              </Paper>
            )}
          </Box>
        </Container>

        <BottomNavigation />
      </Box>
    </>
  );
};

